import { ManualJournalRecurringRule } from "@common/types/domains/accounting/manualJournalRecurringRule";
import {
    ManualJournalRecurringPeriod,
    MonthRecurrenceType,
} from "@common/schemas/domains/accounting/manualJournalRecurringRule";
import {
    addDays,
    endOfDay,
    endOfMonth,
    format,
    getDate,
    isBefore,
    parseISO,
    setDate,
    setDay,
    startOfMonth,
} from "date-fns";
import {
    fullDateFullYearFormat,
    getDaySuffix,
    getUTCDateForPST,
} from "@common/helpers/date";
import { pluralize } from "@common/helpers/string";

export function getDateByRecurringRule(
    recurringRule: ManualJournalRecurringRule,
    date: Date,
) {
    const { period, weekDay, month, dateOfMonth, monthRecurrenceType } =
        recurringRule;

    switch (period) {
        case ManualJournalRecurringPeriod.DAILY:
            return date;

        case ManualJournalRecurringPeriod.WEEKLY:
            if (typeof weekDay === "number") {
                return setDay(date, weekDay);
            }
            return date;

        case ManualJournalRecurringPeriod.MONTHLY:
            if (typeof monthRecurrenceType !== "string") {
                return date;
            }

            if (monthRecurrenceType === MonthRecurrenceType.DAY) {
                if (dateOfMonth === null || dateOfMonth === undefined) {
                    return date;
                }
                const lastDayOfMonth = getDate(endOfMonth(date));
                if (dateOfMonth > lastDayOfMonth) {
                    return endOfMonth(date);
                }
                return setDate(date, dateOfMonth);
            } else if (
                monthRecurrenceType ===
                MonthRecurrenceType.LAST_WEEKDAY_OCCURRENCE
            ) {
                let lastOccurrence = endOfMonth(date);
                if (weekDay === null || weekDay === undefined) {
                    return date;
                }
                lastOccurrence = setDay(lastOccurrence, weekDay);

                if (lastOccurrence.getMonth() !== date.getMonth()) {
                    return addDays(lastOccurrence, -7);
                } else {
                    return lastOccurrence;
                }
            } else {
                if (typeof weekDay !== "number") {
                    return date;
                }

                const occurrenceMap = {
                    [MonthRecurrenceType.FIRST_WEEKDAY_OCCURRENCE]: 1,
                    [MonthRecurrenceType.SECOND_WEEKDAY_OCCURRENCE]: 2,
                    [MonthRecurrenceType.THIRD_WEEKDAY_OCCURRENCE]: 3,
                    [MonthRecurrenceType.FOURTH_WEEKDAY_OCCURRENCE]: 4,
                };

                const occurrence = occurrenceMap[monthRecurrenceType];

                const firstDayOfMonth = endOfDay(startOfMonth(date));
                let firstOccurrence = setDay(firstDayOfMonth, weekDay);

                if (isBefore(firstOccurrence, firstDayOfMonth)) {
                    firstOccurrence = addDays(firstOccurrence, 7);
                }

                return addDays(firstOccurrence, (occurrence - 1) * 7);
            }

        case ManualJournalRecurringPeriod.YEARLY:
            if (typeof month === "number" && typeof dateOfMonth === "number") {
                date.setMonth(month);
                date.setDate(dateOfMonth);
                return date;
            }
            return date;

        default:
            return date;
    }
}

const PERIOD_LABELS: Record<ManualJournalRecurringPeriod, string> = {
    [ManualJournalRecurringPeriod.DAILY]: "Day",
    [ManualJournalRecurringPeriod.WEEKLY]: "Week",
    [ManualJournalRecurringPeriod.MONTHLY]: "Month",
    [ManualJournalRecurringPeriod.YEARLY]: "Year",
};

const WEEK_DAY_LABELS: Record<number, string> = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
};

const MONTH_LABELS: Record<number, string> = {
    0: "January",
    1: "February",
    2: "March",
    3: "April",
    4: "May",
    5: "June",
    6: "July",
    7: "August",
    8: "September",
    9: "October",
    10: "November",
    11: "December",
};

export function formatRecurringRuleLabel(
    rule: Partial<ManualJournalRecurringRule>,
): string {
    const {
        period,
        interval,
        endDate,
        maxOccurrenceCount,
        weekDay,
        monthRecurrenceType,
        dateOfMonth,
        month,
    } = rule;
    if (!period || !interval) {
        return "";
    }

    const periodLabel = PERIOD_LABELS[period];
    if (!periodLabel) {
        return "";
    }

    const intervalString = interval > 1 ? ` ${interval}` : "";
    const prefix = `Every${intervalString} ${pluralize(periodLabel, interval).toLowerCase()}`;

    const weekDayLabel =
        typeof weekDay === "number" ? WEEK_DAY_LABELS[weekDay] : undefined;
    const monthRecurrenceTypeSuffix = monthRecurrenceType
        ? ` ${monthRecurrenceType.toLowerCase()}`
        : "";
    const weekDaySuffix = weekDayLabel
        ? ` on${monthRecurrenceTypeSuffix} ${weekDayLabel}`
        : "";

    const suffixEndDate = endDate
        ? ` until ${format(getUTCDateForPST(parseISO(endDate)), fullDateFullYearFormat)}`
        : "";

    let daySuffix = "";
    if (
        monthRecurrenceType === MonthRecurrenceType.DAY &&
        typeof dateOfMonth === "number"
    ) {
        daySuffix =
            dateOfMonth === 31
                ? " on the last day"
                : ` on ${dateOfMonth}${getDaySuffix(dateOfMonth)} day`;
    }

    const suffixMaxOccurrenceCount = maxOccurrenceCount
        ? ` for ${maxOccurrenceCount} ${pluralize("recurrence", maxOccurrenceCount)}`
        : "";

    const monthLabel =
        typeof month === "number" ? MONTH_LABELS[month] : undefined;
    const dayLabel =
        typeof dateOfMonth === "number"
            ? `${dateOfMonth}${getDaySuffix(dateOfMonth)}`
            : undefined;
    const yearDaySuffix =
        dayLabel && monthLabel ? ` on ${monthLabel} ${dayLabel}` : "";

    return `${prefix}${weekDaySuffix}${daySuffix}${yearDaySuffix}${suffixEndDate}${suffixMaxOccurrenceCount}`;
}
